// Inventory insights and analytics hooks
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { isAssistantLoggedIn } from '@/lib/permissions'

export interface InventoryInsight {
  totalIngredients: number
  lowStockItems: Array<{
    ingredientId: string
    nameAr: string
    nameFr: string
    emoji: string
    quantity: number
    unit: string
    minThreshold: number
  }>
  outOfStockItems: Array<{
    ingredientId: string
    nameAr: string
    nameFr: string
    emoji: string
  }>
  totalInventoryValue: number
  recentDeductions: number
  topConsumedIngredients: Array<{
    ingredientId: string
    ingredientName: string
    totalDeducted: number
    unit: string
  }>
}

export interface InventoryEvolutionPoint {
  date: string
  ingredientId: string
  ingredientName: string
  quantity: number
  unit: string
}

export interface PlateConsumptionBreakdown {
  menuItemId: string
  nameAr: string
  nameFr: string
  optionId: string | null
  optionNameAr: string | null
  optionNameFr: string | null
  totalOrdered: number
  ingredientPerPlate: number
  totalIngredientConsumed: number
  unit: string
}

export interface IngredientConsumption {
  ingredientId: string
  nameAr: string
  nameFr: string
  emoji: string
  totalConsumed: number
  unit: string
  averagePerDay: number
}

/**
 * Fetch inventory insights for a restaurant
 * Supports both admin users and assistants with appropriate permissions
 */
export function useInventoryInsights(restaurantId: string | null) {
  return useQuery({
    queryKey: ['inventory', 'insights', restaurantId],
    queryFn: async (): Promise<InventoryInsight> => {
      if (!restaurantId) {
        return {
          totalIngredients: 0,
          lowStockItems: [],
          outOfStockItems: [],
          totalInventoryValue: 0,
          recentDeductions: 0,
          topConsumedIngredients: [],
        }
      }

      let inventory: any[] = []

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Use assistant RPC function for inventory
        const { data: assistantInventory, error: assistantError } = await supabase.rpc('assistant_get_inventory', {
          p_restaurant_id: restaurantId,
        })

        if (assistantError) {
          // If permission denied, return empty results
          if (assistantError.message?.includes('PERMISSION_DENIED')) {
            return {
              totalIngredients: 0,
              lowStockItems: [],
              outOfStockItems: [],
              totalInventoryValue: 0,
              recentDeductions: 0,
              topConsumedIngredients: [],
            }
          }
          throw assistantError
        }

        // Fetch ingredients separately for assistant using RPC function
        // Assistants don't have direct table access, so we use the RPC function
        const { data: ingredients, error: ingredientsError } = await supabase.rpc('assistant_get_ingredients', {
          p_restaurant_id: restaurantId,
        })

        if (ingredientsError) {
          // Silently fail - ingredients will be null and handled gracefully
        }

        const ingredientsMap = new Map((ingredients || []).map((ing: any) => [ing.id, ing]))

        // Map to same structure as admin query
        inventory = (assistantInventory || []).map((i: any) => {
          const ingredient = ingredientsMap.get(i.ingredient_id)
          return {
            id: i.id,
            quantity: i.quantity,
            min_threshold: i.min_threshold || 0,
            unit: i.unit,
            ingredient: ingredient ? {
              id: ingredient.id,
              name_ar: ingredient.name_ar || '',
              name_fr: ingredient.name_fr || '',
              emoji: ingredient.emoji || '🍽️',
            } : null,
          }
        })
      } else {
        // Admin user - use direct query
        const { data: inventoryData, error: inventoryError } = await supabase
          .from('inventory')
          .select(`
            id,
            quantity,
            min_threshold,
            unit,
            ingredient:ingredients (
              id,
              name_ar,
              name_fr,
              emoji
            )
          `)
          .eq('restaurant_id', restaurantId)

        if (inventoryError) {
          throw inventoryError
        }

        inventory = inventoryData || []
      }

      const totalIngredients = inventory.length

      const lowStockItems = inventory
        .filter((inv: any) => inv.quantity <= inv.min_threshold && inv.quantity > 0)
        .map((inv: any) => ({
          ingredientId: inv.ingredient?.id || '',
          nameAr: inv.ingredient?.name_ar || '',
          nameFr: inv.ingredient?.name_fr || '',
          emoji: inv.ingredient?.emoji || '🍽️',
          quantity: inv.quantity,
          unit: inv.unit,
          minThreshold: inv.min_threshold,
        }))

      const outOfStockItems = inventory
        .filter((inv: any) => inv.quantity <= 0)
        .map((inv: any) => ({
          ingredientId: inv.ingredient?.id || '',
          nameAr: inv.ingredient?.name_ar || '',
          nameFr: inv.ingredient?.name_fr || '',
          emoji: inv.ingredient?.emoji || '🍽️',
        }))

      // Get recent deductions (last 7 days)
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

      const { data: historyData, error: historyError } = await (supabase as any)
        .from('inventory_history')
        .select(`
          id,
          previous_quantity,
          new_quantity,
          change_type,
          ingredient_id,
          ingredient:ingredients (
            id,
            name_ar,
            name_fr
          ),
          inventory:inventory (
            unit
          )
        `)
        .eq('restaurant_id', restaurantId)
        .in('change_type', ['order_in_progress', 'order_ready'])
        .gte('created_at', sevenDaysAgo.toISOString())

      if (historyError) {
        throw historyError
      }

      const recentDeductions = historyData?.length || 0

      // Calculate top consumed ingredients
      const consumptionMap = new Map<string, { name: string; total: number; unit: string }>()
      
      historyData?.forEach((record: any) => {
        const deducted = record.previous_quantity - record.new_quantity
        const ingredientId = record.ingredient_id || record.ingredient?.id
        const ingredientName = record.ingredient?.name_ar || record.ingredient?.name_fr || 'Unknown'
        const unit = record.inventory?.unit || 'unit'
        
        if (ingredientId && deducted > 0) {
          const existing = consumptionMap.get(ingredientId)
          if (existing) {
            consumptionMap.set(ingredientId, {
              ...existing,
              total: existing.total + deducted,
            })
          } else {
            consumptionMap.set(ingredientId, {
              name: ingredientName,
              total: deducted,
              unit: unit,
            })
          }
        }
      })

      const topConsumedIngredients = Array.from(consumptionMap.entries())
        .map(([ingredientId, data]) => ({
          ingredientId,
          ingredientName: data.name,
          totalDeducted: data.total,
          unit: data.unit,
        }))
        .sort((a, b) => b.totalDeducted - a.totalDeducted)
        .slice(0, 5)

      // Calculate total inventory value (simplified - would need ingredient prices)
      const totalInventoryValue = 0 // TODO: Add ingredient pricing if needed

      return {
        totalIngredients,
        lowStockItems,
        outOfStockItems,
        totalInventoryValue,
        recentDeductions,
        topConsumedIngredients,
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!restaurantId,
  })
}

/**
 * Fetch inventory evolution over time
 */
export function useInventoryEvolution(
  restaurantId: string | null,
  days: number = 30,
  ingredientIds?: string[]
) {
  return useQuery({
    queryKey: ['inventory', 'evolution', restaurantId, days, ingredientIds],
    queryFn: async (): Promise<InventoryEvolutionPoint[]> => {
      if (!restaurantId) {
        return []
      }

      const startDate = new Date()
      startDate.setDate(startDate.getDate() - days)

      let query = (supabase as any)
        .from('inventory_history')
        .select(`
          created_at,
          new_quantity,
          ingredient_id,
          ingredient:ingredients!inventory_history_ingredient_id_fkey (
            id,
            name_ar,
            name_fr
          ),
          inventory:inventory!inventory_history_inventory_id_fkey (
            unit
          )
        `)
        .eq('restaurant_id', restaurantId)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true })

      if (ingredientIds && ingredientIds.length > 0) {
        query = query.in('ingredient_id', ingredientIds)
      }

      const { data, error } = await query

      if (error) {
        throw error
      }

      return (data || []).map((record: any) => ({
        date: record.created_at,
        ingredientId: record.ingredient_id || record.ingredient?.id || '',
        ingredientName: record.ingredient?.name_ar || record.ingredient?.name_fr || 'Unknown',
        quantity: record.new_quantity,
        unit: record.inventory?.unit || 'unit',
      }))
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!restaurantId,
  })
}

/**
 * Fetch ingredient consumption for a restaurant
 */
export function useIngredientConsumption(
  restaurantId: string | null,
  timePeriod: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'daily'
) {
  return useQuery({
    queryKey: ['inventory', 'consumption', restaurantId, timePeriod],
    queryFn: async (): Promise<IngredientConsumption[]> => {
      if (!restaurantId) {
        return []
      }

      const now = new Date()
      let startDate = new Date()
      let days = 1

      switch (timePeriod) {
        case 'daily':
          startDate.setHours(0, 0, 0, 0)
          days = 1
          break
        case 'weekly':
          startDate.setDate(now.getDate() - 7)
          days = 7
          break
        case 'monthly':
          startDate.setDate(now.getDate() - 30)
          days = 30
          break
        case 'yearly':
          startDate.setDate(now.getDate() - 365)
          days = 365
          break
      }

      // Get consumption data from inventory history
      const { data: historyData, error: historyError } = await (supabase as any)
        .from('inventory_history')
        .select(`
          ingredient_id,
          previous_quantity,
          new_quantity,
          change_type,
          ingredient:ingredients (
            id,
            name_ar,
            name_fr,
            emoji
          ),
          inventory:inventory (
            unit
          )
        `)
        .eq('restaurant_id', restaurantId)
        .in('change_type', ['order_in_progress', 'order_ready'])
        .gte('created_at', startDate.toISOString())

      if (historyError) {
        throw historyError
      }

      // Calculate consumption per ingredient
      const consumptionMap = new Map<string, {
        nameAr: string
        nameFr: string
        emoji: string
        totalConsumed: number
        unit: string
      }>()

      historyData?.forEach((record: any) => {
        const consumed = record.previous_quantity - record.new_quantity
        const ingredientId = record.ingredient_id || record.ingredient?.id
        const nameAr = record.ingredient?.name_ar || ''
        const nameFr = record.ingredient?.name_fr || ''
        const emoji = record.ingredient?.emoji || '🍽️'
        const unit = record.inventory?.unit || 'unit'

        if (ingredientId && consumed > 0) {
          const existing = consumptionMap.get(ingredientId)
          if (existing) {
            consumptionMap.set(ingredientId, {
              ...existing,
              totalConsumed: existing.totalConsumed + consumed,
            })
          } else {
            consumptionMap.set(ingredientId, {
              nameAr,
              nameFr,
              emoji,
              totalConsumed: consumed,
              unit,
            })
          }
        }
      })

      return Array.from(consumptionMap.entries())
        .map(([ingredientId, data]) => ({
          ingredientId,
          nameAr: data.nameAr,
          nameFr: data.nameFr,
          emoji: data.emoji,
          totalConsumed: data.totalConsumed,
          unit: data.unit,
          averagePerDay: data.totalConsumed / days,
        }))
        .sort((a, b) => b.totalConsumed - a.totalConsumed)
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!restaurantId,
  })
}

/**
 * Fetch plate (menu item) breakdown for a specific ingredient's consumption
 */
export function useIngredientPlateBreakdown(
  restaurantId: string | null,
  ingredientId: string | undefined,
  timePeriod: 'daily' | 'weekly' | 'monthly' | 'yearly',
  enabled: boolean
) {
  return useQuery({
    queryKey: ['inventory', 'plate-breakdown', restaurantId, ingredientId, timePeriod],
    queryFn: async (): Promise<PlateConsumptionBreakdown[]> => {
      if (!restaurantId || !ingredientId) return []

      const now = new Date()
      const startDate = new Date()

      switch (timePeriod) {
        case 'daily':
          startDate.setHours(0, 0, 0, 0)
          break
        case 'weekly':
          startDate.setDate(now.getDate() - 7)
          break
        case 'monthly':
          startDate.setDate(now.getDate() - 30)
          break
        case 'yearly':
          startDate.setDate(now.getDate() - 365)
          break
      }

      // Parallel: get inventory history order_ids + recipe info (including option-specific)
      const [historyResult, recipeResult] = await Promise.all([
        (supabase as any)
          .from('inventory_history')
          .select('order_id')
          .eq('restaurant_id', restaurantId)
          .eq('ingredient_id', ingredientId)
          .in('change_type', ['order_in_progress', 'order_ready'])
          .gte('created_at', startDate.toISOString())
          .not('order_id', 'is', null),
        (supabase as any)
          .from('menu_item_ingredients')
          .select(`
            menu_item_id,
            menu_item_option_id,
            quantity,
            unit,
            menu_item_option:menu_item_options (
              id,
              name_ar,
              name_fr
            )
          `)
          .eq('ingredient_id', ingredientId),
      ])

      if (historyResult.error) throw historyResult.error
      if (recipeResult.error) throw recipeResult.error

      const orderIds = [...new Set((historyResult.data || []).map((r: any) => r.order_id))] as string[]
      const recipes = (recipeResult.data || []) as Array<{
        menu_item_id: string
        menu_item_option_id: string | null
        quantity: number
        unit: string
        menu_item_option: { id: string; name_ar: string; name_fr: string } | null
      }>

      if (orderIds.length === 0 || recipes.length === 0) return []

      // Create maps for base recipes (option_id = null) and option-specific recipes
      const baseRecipeMap = new Map<string, { quantity: number; unit: string }>()
      const optionRecipeMap = new Map<string, { quantity: number; unit: string; nameAr: string; nameFr: string }>()
      const menuItemIds: string[] = []

      recipes.forEach((r) => {
        if (!menuItemIds.includes(r.menu_item_id)) {
          menuItemIds.push(r.menu_item_id)
        }
        if (r.menu_item_option_id === null) {
          // Base recipe (no option)
          baseRecipeMap.set(r.menu_item_id, { quantity: r.quantity, unit: r.unit })
        } else {
          // Option-specific recipe
          const key = `${r.menu_item_id}:${r.menu_item_option_id}`
          optionRecipeMap.set(key, {
            quantity: r.quantity,
            unit: r.unit,
            nameAr: r.menu_item_option?.name_ar || '',
            nameFr: r.menu_item_option?.name_fr || '',
          })
        }
      })

      // Get order_items for these orders and menu items, with menu item names and option info
      const { data: orderItemsData, error: orderItemsError } = await (supabase as any)
        .from('order_items')
        .select(`
          menu_item_id,
          menu_item_option_id,
          quantity,
          menu_item:menu_items (
            id,
            name_ar,
            name_fr
          ),
          menu_item_option:menu_item_options (
            id,
            name_ar,
            name_fr
          )
        `)
        .in('order_id', orderIds)
        .in('menu_item_id', menuItemIds)

      if (orderItemsError) throw orderItemsError

      // Aggregate by (menu_item_id, menu_item_option_id) pair
      const aggregation = new Map<string, {
        menuItemId: string
        optionId: string | null
        nameAr: string
        nameFr: string
        optionNameAr: string | null
        optionNameFr: string | null
        totalOrdered: number
      }>()

      ;(orderItemsData || []).forEach((item: any) => {
        const mid = item.menu_item_id
        const optId = item.menu_item_option_id || null
        const key = `${mid}:${optId || 'base'}`

        const existing = aggregation.get(key)
        if (existing) {
          existing.totalOrdered += item.quantity
        } else {
          aggregation.set(key, {
            menuItemId: mid,
            optionId: optId,
            nameAr: item.menu_item?.name_ar || '',
            nameFr: item.menu_item?.name_fr || '',
            optionNameAr: item.menu_item_option?.name_ar || null,
            optionNameFr: item.menu_item_option?.name_fr || null,
            totalOrdered: item.quantity,
          })
        }
      })

      const results: PlateConsumptionBreakdown[] = []
      for (const [, agg] of aggregation.entries()) {
        // Calculate ingredient per plate: base amount + option-specific amount (if option selected)
        const baseRecipe = baseRecipeMap.get(agg.menuItemId)
        const baseAmount = baseRecipe?.quantity || 0
        const unit = baseRecipe?.unit || 'g'

        let optionAmount = 0
        if (agg.optionId) {
          const optionKey = `${agg.menuItemId}:${agg.optionId}`
          const optionRecipe = optionRecipeMap.get(optionKey)
          optionAmount = optionRecipe?.quantity || 0
        }

        const ingredientPerPlate = baseAmount + optionAmount

        // Only include if this menu item actually uses this ingredient
        if (ingredientPerPlate > 0) {
          results.push({
            menuItemId: agg.menuItemId,
            nameAr: agg.nameAr,
            nameFr: agg.nameFr,
            optionId: agg.optionId,
            optionNameAr: agg.optionNameAr,
            optionNameFr: agg.optionNameFr,
            totalOrdered: agg.totalOrdered,
            ingredientPerPlate,
            totalIngredientConsumed: agg.totalOrdered * ingredientPerPlate,
            unit,
          })
        }
      }

      return results.sort((a, b) => b.totalOrdered - a.totalOrdered)
    },
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: enabled && !!restaurantId && !!ingredientId,
  })
}
